import { useEffect, useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import {
  kalipOlustur,
  kalipGuncelle,
  kalipSaati,
  type VardiyaKalibi,
  type KalipOlusturmaData,
} from '@/lib/services/vardiya-kalibi-service'
import type { Sube, Pozisyon } from '@/types'

interface Props {
  acik: boolean
  kalip: VardiyaKalibi | null
  subeler: Sube[]
  pozisyonlar: Pozisyon[]
  onKapat: () => void
  onKaydedildi: () => void
}

interface FormState {
  subeId: string
  isim: string
  baslangic: string
  bitis: string
  pozisyonId: string
}

const BOS: FormState = {
  subeId: '',
  isim: '',
  baslangic: '08:00',
  bitis: '18:00',
  pozisyonId: '',
}

export function VardiyaKalibiFormModal({
  acik, kalip, subeler, pozisyonlar, onKapat, onKaydedildi,
}: Props) {
  const [form, setForm] = useState<FormState>(BOS)
  const [hatalar, setHatalar] = useState<Record<string, string>>({})
  const [hata, setHata] = useState<string | null>(null)
  const [kaydediliyor, setKaydediliyor] = useState(false)

  useEffect(() => {
    if (kalip) {
      setForm({
        subeId: kalip.subeId,
        isim: kalip.isim,
        baslangic: kalip.baslangic,
        bitis: kalip.bitis,
        pozisyonId: kalip.pozisyonId ?? '',
      })
    } else {
      const ilk = subeler[0]?.id ?? ''
      setForm({ ...BOS, subeId: ilk })
    }
    setHatalar({})
    setHata(null)
  }, [kalip, acik, subeler])

  function dogrula(): boolean {
    const e: Record<string, string> = {}
    if (!form.subeId) e.subeId = 'Şube seç'
    if (!form.isim.trim()) e.isim = 'İsim zorunlu'
    if (!form.baslangic) e.baslangic = 'Başlangıç saati zorunlu'
    if (!form.bitis) e.bitis = 'Bitiş saati zorunlu'
    setHatalar(e)
    return Object.keys(e).length === 0
  }

  async function kaydet(ev: React.FormEvent) {
    ev.preventDefault()
    if (!dogrula()) return
    setKaydediliyor(true)
    setHata(null)
    try {
      const data: KalipOlusturmaData = {
        subeId: form.subeId,
        isim: form.isim,
        baslangic: form.baslangic,
        bitis: form.bitis,
        pozisyonId: form.pozisyonId || undefined,
      }
      if (kalip) {
        await kalipGuncelle(kalip.id, data)
      } else {
        await kalipOlustur(data)
      }
      onKaydedildi()
    } catch (err) {
      console.error(err)
      setHata('Kaydedilemedi: ' + (err as Error).message)
    } finally {
      setKaydediliyor(false)
    }
  }

  const sureSaat = kalipSaati({ baslangic: form.baslangic, bitis: form.bitis })
  const geceVardiyasi =
    form.baslangic && form.bitis &&
    form.bitis.localeCompare(form.baslangic) <= 0

  return (
    <Modal
      acik={acik}
      onKapat={onKapat}
      baslik={kalip ? 'Kalıbı Düzenle' : 'Yeni Vardiya Kalıbı'}
      boyut="md"
    >
      <form onSubmit={kaydet} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Şube <span className="text-red-500">*</span>
          </label>
          <select
            value={form.subeId}
            onChange={(e) => setForm((f) => ({ ...f, subeId: e.target.value }))}
            className="input-base"
          >
            <option value="">Seç...</option>
            {subeler.map((s) => (
              <option key={s.id} value={s.id}>{s.isim}</option>
            ))}
          </select>
          {hatalar.subeId && <p className="text-xs text-red-600 mt-1">{hatalar.subeId}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Kalıp Adı <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.isim}
            onChange={(e) => setForm((f) => ({ ...f, isim: e.target.value }))}
            className="input-base"
            placeholder="Örn: Sabah Kasiyer, Gece Rafçı"
          />
          {hatalar.isim && <p className="text-xs text-red-600 mt-1">{hatalar.isim}</p>}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Başlangıç <span className="text-red-500">*</span>
            </label>
            <input
              type="time"
              value={form.baslangic}
              onChange={(e) => setForm((f) => ({ ...f, baslangic: e.target.value }))}
              className="input-base"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Bitiş <span className="text-red-500">*</span>
            </label>
            <input
              type="time"
              value={form.bitis}
              onChange={(e) => setForm((f) => ({ ...f, bitis: e.target.value }))}
              className="input-base"
            />
          </div>
        </div>

        {sureSaat > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm text-blue-900">
            ⏱️ Vardiya süresi: <strong>{sureSaat} saat</strong>
            {geceVardiyasi && ' (gece vardiyası — ertesi gün biter)'}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Pozisyon (opsiyonel)
          </label>
          <select
            value={form.pozisyonId}
            onChange={(e) => setForm((f) => ({ ...f, pozisyonId: e.target.value }))}
            className="input-base"
          >
            <option value="">— Pozisyonsuz —</option>
            {pozisyonlar.map((p) => (
              <option key={p.id} value={p.id}>{p.isim}</option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Pozisyon seçersen, bu kalıba atanan personel o gün için bu pozisyonda sayılır.
          </p>
        </div>

        {hata && (
          <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-xl p-3">
            {hata}
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onKapat} className="btn-secondary flex-1" disabled={kaydediliyor}>
            İptal
          </button>
          <button type="submit" className="btn-primary flex-1" disabled={kaydediliyor}>
            {kaydediliyor ? 'Kaydediliyor...' : kalip ? 'Güncelle' : 'Ekle'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
